import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useT } from "@/i18n/I18nProvider";
import { SEO } from "@/components/common/SEO";
import { PageHero } from "@/components/common/PageHero";
import { ContactCta } from "@/components/common/DetailPage";
import { imageFor } from "@/lib/imagery";
import { COMPANY } from "@/data/company";
import type { RouteKey } from "@/lib/routes";

const RELATED: RouteKey[] = ["compliance.corporate", "compliance.kyc"];

interface Row {
  label: string;
  value: string;
  href?: string;
}

function contentFor(locale: "en" | "it") {
  const en = locale === "en";

  const channels: Row[] = [
    { label: en ? "Website" : "Sito web", value: COMPANY.websiteLabel, href: COMPANY.website },
    { label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
    { label: "PEC", value: COMPANY.pec, href: `mailto:${COMPANY.pec}` },
    { label: en ? "Telephone" : "Telefono", value: COMPANY.phone, href: COMPANY.phoneHref },
  ];

  const warningSigns = en
    ? [
        "Requests for an advance, \"transfer\" or \"release\" fee in order to release goods or execute a contract.",
        "Payment instructions directing funds to a personal account, or to an account not held in the name of BNM S.R.L.",
        "Documents sent from an email domain other than bnmgroup.it or our PEC.",
        "Registration details (VAT, REA, EORI, address) that do not match those on our KYC / Verification page.",
        "Unusually favourable terms combined with pressure to act quickly or confidentially.",
      ]
    : [
        "Richieste di un anticipo, di una \"commissione\" o di una \"tassa di rilascio\" per liberare merci o eseguire un contratto.",
        "Istruzioni di pagamento verso un conto personale o verso un conto non intestato a BNM S.R.L.",
        "Documenti inviati da un dominio diverso da bnmgroup.it o dalla nostra PEC.",
        "Dati di registrazione (partita IVA, REA, EORI, indirizzo) non corrispondenti a quelli della pagina KYC / Verifica.",
        "Condizioni insolitamente favorevoli unite a pressioni ad agire in fretta o in modo riservato.",
      ];

  const neverDo = en
    ? [
        "Request an advance \"fee\" as a condition to release goods or execute a contract.",
        "Ask for payment to a personal account or to an account not held in the name of BNM S.R.L.",
        "Conduct official business through free email domains or a domain other than bnmgroup.it and our PEC.",
        "Pressure a counterparty to bypass standard verification, contractual or payment procedures.",
      ]
    : [
        "Richiedere una \"commissione\" anticipata come condizione per liberare merci o eseguire un contratto.",
        "Chiedere pagamenti verso un conto personale o non intestato a BNM S.R.L.",
        "Svolgere attività ufficiali tramite domini email gratuiti o diversi da bnmgroup.it e dalla nostra PEC.",
        "Spingere una controparte ad aggirare le normali procedure di verifica, contrattuali o di pagamento.",
      ];

  const report = en
    ? [
        "Do not make any payment or release any document.",
        "Confirm the sender against the identifiers on our KYC / Verification page.",
        "Contact us through info@bnmgroup.it, our PEC (bnmsrl@arubapec.it) or +39 349 704 3345, using the details on this website rather than those in the suspicious message.",
        "Report the attempt to us and, where appropriate, to the relevant authorities in your jurisdiction.",
      ]
    : [
        "Non effettuare alcun pagamento e non consegnare alcun documento.",
        "Verifica il mittente rispetto agli identificativi della pagina KYC / Verifica.",
        "Contattaci tramite info@bnmgroup.it, la nostra PEC (bnmsrl@arubapec.it) o +39 349 704 3345, utilizzando i recapiti presenti su questo sito e non quelli del messaggio sospetto.",
        "Segnala il tentativo a noi e, ove opportuno, alle autorità competenti della tua giurisdizione.",
      ];

  return {
    labels: {
      purpose: en ? "Purpose" : "Finalità",
      channels: en ? "Our only genuine channels" : "I nostri unici canali ufficiali",
      warning: en ? "Warning signs" : "Segnali di allarme",
      never: en ? "What BNM S.r.l. will never do" : "Cosa BNM S.r.l. non farà mai",
      report: en ? "How to verify and report" : "Come verificare e segnalare",
    },
    purpose: en
      ? "Companies active in international trade are frequently impersonated by third parties. This notice sets out BNM S.r.l.'s only genuine channels and how to recognise fraudulent activity carried out in our name. It is a standing precaution and does not imply that any specific incident has occurred."
      : "Le società attive nel commercio internazionale sono spesso oggetto di impersonificazione da parte di terzi. Il presente avviso indica gli unici canali autentici di BNM S.r.l. e come riconoscere attività fraudolente svolte a suo nome. Si tratta di una misura precauzionale permanente e non implica che si sia verificato alcun episodio specifico.",
    channels,
    channelsNote: en
      ? "Messages from free or look-alike domains are not official BNM channels."
      : "I messaggi provenienti da domini gratuiti o simili non sono canali ufficiali di BNM.",
    warningSigns,
    neverDo,
    report,
    disclaimer: en
      ? "This notice is provided for information and protection only and does not constitute legal advice."
      : "Il presente avviso è fornito a solo scopo informativo e di tutela e non costituisce consulenza legale.",
  };
}

function MarkerList({ items, testId }: { items: string[]; testId: string }) {
  return (
    <ul className="flex flex-col border-t border-bone-500" data-testid={testId}>
      {items.map((item, idx) => (
        <li key={idx} className="grid grid-cols-12 gap-4 md:gap-8 border-b border-bone-500 py-5">
          <span className="col-span-1 font-mono text-[11px] text-clay pt-1" aria-hidden>
            —
          </span>
          <p className="col-span-11 text-slate2 leading-relaxed text-[15px]">{item}</p>
        </li>
      ))}
    </ul>
  );
}

export default function AntiFraud() {
  const { t, path, locale } = useT();
  const img = imageFor("compliance.antifraud");
  const c = contentFor(locale);

  return (
    <>
      <SEO routeKey="compliance.antifraud" />
      <PageHero routeKey="compliance.antifraud" imageUrl={img?.url} imageAlt={img?.alt} />

      <section className="container-bnm py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <aside className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
            <p className="eyebrow mb-4">{t.sections.scopeLabel}</p>
            <p className="text-slate2 max-w-prose-tight leading-relaxed">
              {(t.routes["compliance.antifraud"] as { lede?: string }).lede}
            </p>
          </aside>

          <div className="lg:col-span-8 flex flex-col gap-12">
            <div data-testid="antifraud-purpose">
              <p className="eyebrow mb-4">{c.labels.purpose}</p>
              <p className="text-ink text-lg md:text-xl font-display font-light leading-snug max-w-prose">
                {c.purpose}
              </p>
            </div>

            <div data-testid="antifraud-channels">
              <p className="eyebrow mb-5">{c.labels.channels}</p>
              <dl className="flex flex-col">
                {c.channels.map((row) => (
                  <div
                    key={row.label}
                    className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-6 border-b border-bone-500 py-3"
                  >
                    <dt className="font-mono text-[11px] uppercase tracking-eyebrow text-slate2 shrink-0">
                      {row.label}
                    </dt>
                    <dd className="text-ink text-sm sm:text-right">
                      {row.href ? (
                        <a
                          href={row.href}
                          className="hover:text-clay transition-colors border-b border-transparent hover:border-clay"
                          {...(row.href.startsWith("http")
                            ? { target: "_blank", rel: "noopener noreferrer" }
                            : {})}
                        >
                          {row.value}
                        </a>
                      ) : (
                        row.value
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
              <p className="mt-4 text-sm text-slate2 leading-relaxed max-w-prose">{c.channelsNote}</p>
            </div>

            <div>
              <p className="eyebrow mb-5">{c.labels.warning}</p>
              <MarkerList items={c.warningSigns} testId="antifraud-warning" />
            </div>

            <div>
              <p className="eyebrow mb-5">{c.labels.never}</p>
              <MarkerList items={c.neverDo} testId="antifraud-never" />
            </div>

            <div data-testid="antifraud-report">
              <p className="eyebrow mb-5">{c.labels.report}</p>
              <ol className="flex flex-col border-t border-bone-500">
                {c.report.map((step, idx) => (
                  <li
                    key={idx}
                    className="grid grid-cols-12 gap-4 md:gap-8 border-b border-bone-500 py-5"
                  >
                    <span className="col-span-2 md:col-span-1 font-mono text-[11px] uppercase tracking-eyebrow text-slate2 pt-1">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <p className="col-span-10 md:col-span-11 text-slate2 leading-relaxed text-[15px]">
                      {step}
                    </p>
                  </li>
                ))}
              </ol>
            </div>

            <div className="border-l-2 border-bone-500 pl-6 py-2" data-testid="antifraud-disclaimer" role="note">
              <p className="text-sm text-slate2 leading-relaxed max-w-prose">{c.disclaimer}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-bone-500 bg-bone-300/40">
        <div className="container-bnm py-16 md:py-20">
          <p className="eyebrow mb-8">{t.sections.seeAlso}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {RELATED.map((k) => (
              <Link
                key={k}
                to={path(k)}
                className="group border border-bone-500 bg-bone p-6 md:p-8 hover:border-ink transition-colors"
                data-testid={`related-${k.replace(".", "-")}`}
              >
                <p className="eyebrow mb-4">{(t.routes[k] as { eyebrow?: string }).eyebrow}</p>
                <h3 className="font-display text-xl md:text-2xl text-ink group-hover:text-clay transition-colors">
                  {t.routes[k].label}
                </h3>
                <span className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-eyebrow text-slate2 group-hover:text-clay transition-colors">
                  <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ContactCta />
    </>
  );
}
